<template>
  <div ref="container" class="relative flex flex-col justify-center w-full overflow-hidden">
    <div class="hidden xl_block">
      <BgDotted class="absolute text-gray-200 transform translate-x-64 -translate-y-8 left-1/3 xl1_left-1/2" />
    </div>

    <div class="relative xl_hidden">
      <BgDottedMobile class="absolute text-gray-200 xl_hidden transform origin-top left-1/2" />
    </div>

    <div class="relative w-min min-w-fit">
      <div v-if="company" class="mb-12">
        <img class="company-logo" data-testid="choose-duration-company-logo" :src="company.logo!" />
      </div>
      <div class="items-center grid grid-cols-12 xl_gap-4">
        <section class="col-span-11 space-y-6 xl1_space-y-8 xl_col-span-6">
          <header class="space-y-3">
            <p class="mb-0 font-semibold tracking-wide uppercase leading-6">Meet with us</p>
            <h2 class="text-2xl font-bold heading">
              <span class="block pb-1 text-lg text-copper-700 md_text-2xl xl1_text-4xl">Book a virtual meeting with</span
              ><span class="block md_text-4xl xl1_text-5xl">{{ person.name }}</span>
            </h2>
          </header>

          <div class="p-4 bg-gray-50 rounded-2xl xl1_p-8" :class="{ 'xl1_grid xl1_grid-cols-12': person.image }">
            <div
              v-if="person.image"
              class="flex items-center mb-8 xl1_col-span-4 xl1_flex-col xl1_justify-center xl1_mb-0 xl1_text-center xl1_mr-8"
            >
              <img class="w-16 h-16 rounded-full lg_w-20 lg_h-20" :src="person.image" :alt="person.name!" data-testid="admin-image" />
              <div class="ml-4 text-sm leading-5 xl1_ml-0 xl1_mt-4">
                <p class="mb-0 font-medium text-gray-900" data-testid="admin-name">{{ person.name }}</p>
                <p v-if="meetingSettings.person" class="mb-0 font-normal text-gray-800" data-testid="admin-title">{{ person.title }}</p>
              </div>
            </div>
            <div class="durations-container" :class="{ 'xl1_col-span-8': person.image }">
              <div v-for="requestType in meetingSettings.meeting_request_types" :key="requestType.id" class="w-full p-2 xl2_w-1/4">
                <VButton
                  class="w-full font-medium sm_h-14 xl1_h-20"
                  size="md"
                  :data-testid="`duration-${requestType.length}`"
                  @click="submit(requestType.length)"
                  >{{ requestType.length }} Minutes</VButton
                >
              </div>
            </div>
          </div>
          <VLink
            v-if="isAskAQuestionEnabled"
            :component="NuxtLink"
            class="text-xs font-semibold xl1_text-sm text-copper-700"
            data-testid="ask-a-question"
            font-weight="normal"
            :to="{ name: 'companies-id', params: { id: company.id }, query: { aaqOpen: true } }"
          >
            Not ready to meet? Ask a question instead
          </VLink>
        </section>
        <section class="hidden xl_block xl_col-span-6">
          <picture>
            <source srcset="~/assets/images/meetings/meetings-av-preview.webp" type="image/webp" />
            <img src="~/assets/images/meetings/meetings-av-preview.png" alt="Audio video meeting preview" />
          </picture>
        </section>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { PropType, DeepReadonly } from 'vue'
import BgDotted from '@/components/icons/BgDotted.svg'
import BgDottedMobile from '@/components/icons/BgDottedMobile.svg'
import type { Company } from '@/entities/company'
import type { ScheduleSetting } from '@/entities/meeting'

const props = defineProps({
  meetingSettings: {
    type: Object as PropType<DeepReadonly<ScheduleSetting>>,
    required: true,
  },
  isAskAQuestionEnabled: {
    type: Boolean,
    default: false,
  },
})
const emit = defineEmits(['choose-duration'])

const route = useRoute()
const { $analytics } = useNuxtApp()
const NuxtLink = resolveComponent('NuxtLink')

const width = ref<number>(0)
const container = ref<HTMLElement | null>(null)
const person = computed(() => ({
  name: props.meetingSettings.person?.name || props.meetingSettings.user?.name,
  title: props.meetingSettings.person?.title,
  image: props.meetingSettings.person?.image,
}))
const company = computed<Partial<Company>>(() => props.meetingSettings.company)

function submit(time: number) {
  $analytics.track('bamInvestorMeetingLengthSelected', {
    duration: time,
    request_setting_id: route.params.id,
    company_id: company.value.id,
  })
  emit('choose-duration', time)
}
</script>

<style lang="stylus" scoped>
.durations-container
  column-width 15rem

.company-logo
  max-width 200px

@screen xl2
  .durations-container
    column-width auto
    display flex
</style>
